import { useEffect, useContext, useState } from 'react';
import { Container, Box, Grid, Card, CardContent, Button } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { Close, LockOpen } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { useCrud, useInteractionsFiles } from '../../../lib/hooks';
import {
    AppContext,
    SttLabelTitle,
    SttLabelForm,
    SttPage,
    SttButtonTicket,
    SttButton,
} from '../../../sporttia/all';
import { getPath } from '../../Pages';
import { formatPriceByLocale, fullName } from '../../../lib/utils';
import { translatePlaceholders } from '../../../lib/translatePlaceholders';
import Occupants from './Occupants';
import HandlePaymentsModal from '../../sc/groups/HandlePaymentsModal';
import {
    buildCancelBookingPaymentOptions,
    canDeleteBookingPayments,
    paymentActionForOption,
    useImpersonalCancelInfo,
} from '../../../components/dialogs/bookingDialog/cancelBookingPaymentOptions';
import { bookingTicketFilename } from './bookingTicketFilename';

export default function Booked() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { id } = useParams();
    const { openFile } = useInteractionsFiles();
    const navigate = useNavigate();
    const [booking, setBooking, , Get, , , Delete] = useCrud<any>();
    const [payments, setPayments] = useState<any[]>();
    const [openingDevice, setOpeningDevice] = useState(false);
    const [ready, setReady] = useState(false);
    const [openAskingPaymentProcedure, setOpenAskingPaymentProcedure] =
        useState(false);
    const [cancelPrivilege, setCancelPrivilege] = useState(false);

    // Las entradas impersonales (al portador) solo pueden devolverse en efectivo
    // o datáfono al cancelar, nunca a monedero; un alquiler mixto no puede
    // cancelarse completo (hay que cancelar cada entrada por separado).
    const { isImpersonal, isMixedImpersonal, canRefundDataphone } =
        useImpersonalCancelInfo(booking?.occupants);

    function loadBooking() {
        Get(`/bookings/${id}`)
            .then((response: any) => {
                setBooking(response.booking);
                cxt.setHeader({
                    title: `${t('labels.booking')}`,
                });
            })
            .catch(() => {});
    }

    function loadPayments() {
        Get(`/bookings/${id}/payments`, { returnChildPayments: true }).then(
            ({ rows }: any) => {
                setPayments(rows);
                cxt.setHeader({
                    title: `${t('labels.booking')}`,
                });
            },
        );
    }

    /**
     * Booking id changes
     */
    useEffect(() => {
        loadBooking();
        loadPayments();
    }, [id]);

    useEffect(() => {
        if (booking && booking.facility) {
            if (
                cxt.user?.role === cxt.constants.roleUser ||
                cxt.user?.role === cxt.constants.roleTeacher
            ) {
                Get(`/scs/${booking.facility.sc.id}/me`)
                    .then(({ mship }: any) => {
                        setCancelPrivilege(
                            cxt.user?.role === cxt.constants.roleSC ||
                                mship.privileges.find(
                                    (priv: any) => priv === 9,
                                ) !== undefined,
                        );
                    })
                    .catch(() => {})
                    .finally(() => {
                        setReady(true);
                    });
            } else {
                setReady(true);
                setCancelPrivilege(true);
            }
        }
    }, [booking]);

    function downloadTicket(format: any) {
        openFile(
            `/bookings/${booking.id}.pdf?paper=${format}`,
            'application/pdf',
            null,
            bookingTicketFilename(booking.id, format),
        );
    }

    const openDevice = (device: any) => {
        cxt.api('POST', `/devices/${device.id}/openings`, {
            success: (r: any) => {
                setOpeningDevice(false);

                if (r.resultFromDevice.result === 'SUCCESS') {
                    cxt.showMessage('S', t('device.opening.opened'));
                } else {
                    cxt.showMessage('S', t('device.opening.error.general'));
                }
            },
            loading: setOpeningDevice,
        });
    };

    const cancelBooking = (deletingPaymentProcedure?: any) => {
        let paymentAction;
        if (deletingPaymentProcedure !== undefined) {
            paymentAction = deletingPaymentProcedure;
        }

        Delete(
            `/bookings/${booking.id}`,
            { paymentAction },
            cxt.user && cxt.user?.role === 'USER',
        )
            .then(() => {
                navigate('/', { replace: true });
            })
            .catch(() => {});
    };

    if (!ready) {
        return null;
    }

    return (
        <Container maxWidth="md">
            {booking && (
                <>
                    <SttPage innerTitle={t('booking.bookingConfirmed')}>
                        <Grid container spacing={3}>
                            <SttLabelForm
                                grid
                                xs={12}
                                sm={6}
                                title={t('facility.facility')}
                                text={booking.facility.name}
                            />

                            <SttLabelForm
                                grid
                                xs={12}
                                sm={6}
                                title={t('sportcenter.sportcenter')}
                                text={`${booking.facility.sc.name}`}
                            />

                            <SttLabelForm
                                grid
                                xs={12}
                                sm={6}
                                title={t('labels.date')}
                                text={`${format(
                                    parseISO(booking.ini),
                                    'EEEE, dd MMMM yyyy',
                                    { locale: getDateLocale() },
                                )}`}
                            />

                            <SttLabelForm
                                grid
                                xs={12}
                                sm={6}
                                title={t('labels.time')}
                                text={`${format(
                                    parseISO(booking.ini),
                                    'HH:mm',
                                )} - ${format(parseISO(booking.end), 'HH:mm')}`}
                            />

                            <SttLabelForm
                                grid
                                sm={6}
                                xs={12}
                                title={t('payment.total')}
                                text={`${formatPriceByLocale(
                                    booking?.price,
                                    booking?.priceShape?.currency,
                                    booking?.priceShape?.locale,
                                )}`}
                            />
                        </Grid>
                    </SttPage>

                    {/* --- Occupants (QR's) --- */}
                    {booking.occupants && (
                        <Occupants
                            occupants={booking.occupants}
                            idOwner={booking.user && booking.user.id}
                            scId={booking.facility.sc.id}
                            isFacilityWithCapacity={
                                booking.facility.maxOccupants > 0
                            }
                            cancelPrivilege={cancelPrivilege}
                        />
                    )}
                </>
            )}
            {/* --- Payments --- */}
            {payments && payments.length > 0 && (
                <Box mt={3}>
                    <Grid size={{ xs: 12 }}>
                        <div>
                            <SttLabelTitle>
                                {t('labels.payments')}
                            </SttLabelTitle>
                        </div>
                        <Box pt={1}>
                            {payments.map((payment: any) => (
                                <Card key={payment.id}>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <SttLabelForm
                                                    title={fullName(
                                                        payment.user,
                                                    )}
                                                    text={translatePlaceholders(
                                                        payment.concept ?? '',
                                                        t,
                                                    )}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, md: 6 }}>
                                                <SttLabelForm
                                                    title={`${formatPriceByLocale(
                                                        payment?.priceShape
                                                            ?.price,
                                                        payment?.priceShape
                                                            ?.currency,
                                                        payment?.priceShape
                                                            ?.locale,
                                                    )}`}
                                                    text={
                                                        <span
                                                            style={
                                                                payment.status !==
                                                                'PAID'
                                                                    ? {
                                                                          color: '#ec3e2d',
                                                                      }
                                                                    : undefined
                                                            }
                                                        >
                                                            {translatePlaceholders(
                                                                payment.statusLang ??
                                                                    '',
                                                                t,
                                                            )}
                                                        </span>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    </Grid>
                </Box>
            )}

            {/* --- Devices --- */}
            {booking &&
                booking.facility &&
                booking.facility.devices &&
                booking.facility.devices.count > 0 && (
                    <Box mt={3}>
                        <Grid size={{ xs: 12 }}>
                            <div>
                                <SttLabelTitle>
                                    {t('device.devices')}
                                </SttLabelTitle>
                            </div>
                            <Box pt={1}>
                                {booking.facility.devices.rows
                                    .filter(
                                        (device: any) => device.remoteOpening,
                                    )
                                    .map((device: any) => (
                                        <Card key={device.id}>
                                            <CardContent>
                                                <Grid container spacing={3}>
                                                    <Grid size={{ xs: 12 }}>
                                                        <SttLabelForm
                                                            title={device.name}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 12 }}>
                                                        <SttButton
                                                            caption={t(
                                                                'device.open',
                                                            )}
                                                            startIcon={
                                                                <LockOpen />
                                                            }
                                                            onClick={() =>
                                                                openDevice(
                                                                    device,
                                                                )
                                                            }
                                                            loading={
                                                                openingDevice
                                                            }
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                            </Box>
                        </Grid>
                    </Box>
                )}

            {/* --- Buttons --- */}
            <Box
                mt={3}
                sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
                <SttButtonTicket
                    caption={`${t('labels.ticket')} A4`}
                    onClick={() => downloadTicket('A4')}
                />

                <SttButtonTicket
                    caption={t('labels.ticket')}
                    onClick={() => downloadTicket('TICKET')}
                />

                <SttButton
                    caption={t('booking.goToFields')}
                    onClick={() =>
                        navigate(
                            `${getPath('timetable', {
                                state: {
                                    id: booking.facility.sc.id,
                                },
                            })}?&date=${format(
                                parseISO(booking.ini),
                                'yyyy-MM-dd',
                            )}`,
                        )
                    }
                />

                {cancelPrivilege && (
                    <Button
                        variant="contained"
                        className="button-delete"
                        onClick={() => {
                            // Un alquiler mixto (invitado + usuarios reales) no
                            // puede cancelarse completo: la devolución difiere
                            // por ocupante. Se cancela cada entrada por separado.
                            if (isMixedImpersonal) {
                                cxt?.showMessage(
                                    'E',
                                    t('impersonalTicket.mixedCancelBlocked'),
                                );
                                return;
                            }

                            const isUserWithoutPayment =
                                cxt?.user?.role === 'USER' ||
                                (cxt?.user?.role === 'SPORTCENTER' &&
                                    (payments?.length ?? 0) <= 0);

                            if (isUserWithoutPayment) cancelBooking(6);
                            else setOpenAskingPaymentProcedure(true);
                        }}
                        startIcon={<Close />}
                    >
                        {t('booking.cancelBooking')}
                    </Button>
                )}
            </Box>

            <HandlePaymentsModal
                warningText={t('booking.howToHandlePayments')}
                open={openAskingPaymentProcedure}
                onClose={() => setOpenAskingPaymentProcedure(false)}
                options={buildCancelBookingPaymentOptions(
                    canDeleteBookingPayments(cxt),
                    t,
                    isImpersonal,
                    canRefundDataphone,
                )}
                onAccept={(option: any) => {
                    cancelBooking(paymentActionForOption(option));
                }}
            />
        </Container>
    );
}
